/**
 * MapErrorBoundary Component
 *
 * React Error Boundary specifically designed for map components.
 * Prevents Leaflet/map errors from crashing the entire panel.
 *
 * Features:
 * - Catches JavaScript errors in child component tree
 * - Shows user-friendly fallback with retry button
 * - Logs errors for debugging
 * - Resets on key change (e.g., when workorders change)
 *
 * @example
 * <MapErrorBoundary resetKey={`${date}-${techId}`}>
 *   <DayPlanningMap workorders={workorders} routes={routes} />
 * </MapErrorBoundary>
 */

import { Component, ReactNode, ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface MapErrorFallbackProps {
  /** Error that was caught */
  error: Error;
  /** Reset the error boundary to try rendering again */
  onRetry: () => void;
  /** Optional className for the container */
  className?: string;
}

/**
 * Fallback UI shown when map component fails
 */
export function MapErrorFallback({
  error,
  onRetry,
  className,
}: MapErrorFallbackProps) {
  return (
    <div
      role="alert"
      className={cn(
        `flex flex-col items-center justify-center rounded-lg border border-amber-500/30
        bg-amber-50/50 p-6 text-center dark:bg-amber-950/20`,
        className,
      )}
    >
      <AlertTriangle className="mb-3 h-8 w-8 text-amber-500" />
      <h3 className="mb-1 text-sm font-medium text-amber-800 dark:text-amber-200">
        Map failed to load
      </h3>
      <p className="mb-4 text-xs text-amber-700 dark:text-amber-300">
        {error.message || 'An error occurred while rendering the map'}
      </p>
      <Button size="sm" variant="outline" onClick={onRetry} className="gap-1.5">
        <RefreshCw className="h-3.5 w-3.5" />
        Retry
      </Button>
    </div>
  );
}

interface MapErrorBoundaryProps {
  /** Child components (typically a map component) */
  children: ReactNode;
  /** Key to reset the error boundary when it changes */
  resetKey?: string;
  /** Optional className for the fallback container */
  fallbackClassName?: string;
}

interface MapErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary for map components.
 * Catches rendering errors and shows a fallback UI with retry option.
 */
export class MapErrorBoundary extends Component<
  MapErrorBoundaryProps,
  MapErrorBoundaryState
> {
  private lastResetKey: string | undefined;

  constructor(props: MapErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
    this.lastResetKey = props.resetKey;
  }

  static getDerivedStateFromError(error: Error): MapErrorBoundaryState {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error for debugging (could be sent to error tracking service)
    // Using a structured format that's easy to search in logs
    if (process.env.NODE_ENV !== 'production') {
      console.error('[MapErrorBoundary] Map component error:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      });
    }
  }

  componentDidUpdate(prevProps: MapErrorBoundaryProps): void {
    // Reset error state when resetKey changes
    if (
      this.state.hasError &&
      this.props.resetKey !== prevProps.resetKey &&
      this.props.resetKey !== this.lastResetKey
    ) {
      this.lastResetKey = this.props.resetKey;
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      return (
        <MapErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
          className={this.props.fallbackClassName}
        />
      );
    }

    return this.props.children;
  }
}
